// @flow

// 번역자 화면의 연결 상태 표시등(#204)이 보는 상태를 모은다.
//
// 앱이 끊김을 아는 경로는 브라우저 offline 이벤트와 라이브러리 keepalive(5분)뿐이라,
// "Wi‑Fi는 붙어 있는데 인터넷이 안 되는" 경우에는 실제로 끊겼는데도 정상으로 보일 수 있다.
// 그래서 10초마다 서울 relay에 확인 요청을 보내고, 연속 2번 실패하면 끊긴 것으로 보고
// AppSync 연결을 끊어 새로 붙게 한다 (조용히 죽은 연결을 20초 안에 잡는다).

import { useEffect, useState } from "react";
import { pingRelay } from "./MessageSender";
import {
  dropRealtimeConnection,
  getAppSyncRetryingCount,
  onAppSyncRetryingChange,
} from "./AppSyncSubscription";
import { getMqttRetryingCount, onMqttRetryingChange } from "./MqttClient";

export const PROBE_INTERVAL_MS = 10 * 1000;
const PROBE_TIMEOUT_MS = 5 * 1000;
export const FAILURES_TO_DROP = 2;
export const SLOW_RTT_MS = 1000;

export type ConnectionStatus = {
  online: boolean,
  // 마지막 확인 요청 왕복 시간. 아직 한 번도 성공 못 했으면 null
  rttMs: ?number,
  // 확인 요청 연속 실패 횟수 (성공하면 0)
  failures: number,
  // 마지막 성공 시각 — 표시등 점을 깜빡이는 데 쓴다
  lastOkAt: ?number,
  checked: boolean,
  appsyncRetrying: number,
  mqttRetrying: number,
};

export type ConnectionLevel = "unknown" | "ok" | "degraded" | "down";

let ping: (timeoutMs: number) => Promise<number> = pingRelay;
let drop: () => void = dropRealtimeConnection;

// 테스트 전용
export function setConnectionDepsForTest(deps: {
  ping: (timeoutMs: number) => Promise<number>,
  drop: () => void,
}) {
  ping = deps.ping;
  drop = deps.drop;
}

const initialState = (): ConnectionStatus => ({
  online: navigator.onLine !== false,
  rttMs: null,
  failures: 0,
  lastOkAt: null,
  checked: false,
  appsyncRetrying: 0,
  mqttRetrying: 0,
});
let state: ConnectionStatus = initialState();
const listeners: Set<(ConnectionStatus) => void> = new Set();

function update(patch: $Shape<ConnectionStatus>) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn(state));
}

export function getConnectionStatus(): ConnectionStatus {
  return state;
}

let inFlight = false;
async function probe() {
  if (inFlight) {
    return;
  }
  // 오프라인이면 보내 봐야 실패한다. offline 이벤트 쪽에서 이미 연결을 끊었다
  if (navigator.onLine === false) {
    update({ online: false, checked: true });
    return;
  }
  inFlight = true;
  try {
    const rttMs = await ping(PROBE_TIMEOUT_MS);
    update({ online: true, rttMs, failures: 0, lastOkAt: Date.now(), checked: true });
  } catch (e) {
    const failures = state.failures + 1;
    update({ failures, checked: true });
    if (failures === FAILURES_TO_DROP) {
      console.warn(
        `[ConnectionStatus] ${failures} probes failed in a row, reconnecting AppSync`
      );
      drop();
    }
  } finally {
    inFlight = false;
  }
}

// 여러 컴포넌트가 써도 확인 요청은 하나만 돈다
let users = 0;
let cleanup: ?() => void = null;

function startMonitor() {
  users += 1;
  if (users > 1) {
    return;
  }
  const onOnline = () => {
    update({ online: true });
    probe(); // 돌아오자마자 확인해 빨리 초록으로
  };
  // 끊기기 전 측정값은 버린다 — 돌아왔을 때 실제로 응답을 받기 전까지 초록을 띄우지 않는다
  const onOffline = () => update({ online: false, rttMs: null, checked: false });
  const syncRetrying = () =>
    update({
      appsyncRetrying: getAppSyncRetryingCount(),
      mqttRetrying: getMqttRetryingCount(),
    });
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  const offAppSync = onAppSyncRetryingChange(syncRetrying);
  const offMqtt = onMqttRetryingChange(syncRetrying);
  syncRetrying();
  probe();
  const timer = setInterval(probe, PROBE_INTERVAL_MS);
  cleanup = () => {
    clearInterval(timer);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    offAppSync();
    offMqtt();
  };
}

// 테스트 전용: 모듈 상태를 처음으로 되돌린다
export function resetConnectionStatusForTest() {
  if (cleanup) {
    cleanup();
  }
  cleanup = null;
  users = 0;
  inFlight = false;
  listeners.clear();
  state = initialState();
}

function stopMonitor() {
  users -= 1;
  if (users === 0 && cleanup) {
    cleanup();
    cleanup = null;
  }
}

// 표시 단계: 빨강(끊김) > 노랑(재연결 중·느림·한 번 실패·전송 재시도) > 초록
export function connectionLevel(
  status: ConnectionStatus,
  sendRetrying: boolean = false
): ConnectionLevel {
  if (!status.online || status.failures >= FAILURES_TO_DROP) {
    return "down";
  }
  if (!status.checked) {
    return "unknown";
  }
  if (
    status.failures > 0 ||
    status.appsyncRetrying > 0 ||
    status.mqttRetrying > 0 ||
    sendRetrying ||
    (status.rttMs != null && status.rttMs >= SLOW_RTT_MS)
  ) {
    return "degraded";
  }
  return status.rttMs == null ? "unknown" : "ok";
}

// 확인 요청만 돌린다 (표시 없이 끊김 감지 + Direct Send Lambda 데우기)
export function useConnectionMonitor() {
  useEffect(() => {
    startMonitor();
    return stopMonitor;
  }, []);
}

export function useConnectionStatus(): ConnectionStatus {
  const [status, setStatus] = useState(getConnectionStatus);
  useEffect(() => {
    listeners.add(setStatus);
    startMonitor();
    setStatus(getConnectionStatus());
    return () => {
      listeners.delete(setStatus);
      stopMonitor();
    };
  }, []);
  return status;
}
